import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { SectionListWithSearchField } from 'components';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { styles as confirmationModalStyles } from 'components/confirmation-modal/confirmation-modal-helper';
import { Controller, useForm } from 'react-hook-form';
import { possiblyNavigateToMetadataSearch } from '../../../../lib';
import {
    combinedSystemAndUserWalletsData,
    createAndUpdateUserGroupDefaults,
    CreateAndUpdateUserGroupFormData,
    CreateAndUpdateUserGroupSchema,
    GroupMembersData,
    ListComponent,
    updateUserGroupStyles,
} from './create-or-edit-user-group-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    useDeleteUserGroup,
    useUpdateUserGroup,
    useUserGroupDetails,
    useWalletList,
} from 'hooks';
import { useAuth, usePermission } from '../../../../core';
import {
    USER_GROUPS,
    walletsAndGroupsCommonStyles,
} from 'pages/wallets-and-groups/wallets-and-groups-helper';
import { LabelAndValueComponent } from '../create-or-edit-user-wallet/user-wallet-from-helper';
import { Fab, TextField } from '@biq/apex-ui';
import { DescriptionTextField } from '../description-field/DescriptionTextField';
import AddIcon from '@mui/icons-material/Add';
import { AddGroupMembers } from './AddGroupMembers';
import { UpdateWalletsAndGroupsWrapper } from '../update-wallets-and-groups-wrapper/UpdateWalletsAndGroupsWrapper';

export const UpdateUserGroup = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const { hasUserCurrentRole } = usePermission();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { id } = useParams();
    const { data: walletsData, isLoading: walletsLoading } = useWalletList();
    const { data: userGroupDetails, isLoading: userGroupDetailsLoading } =
        useUserGroupDetails({
            id: id,
        });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
    const [openAddGroupMemberModal, setOpenAddGroupMemberModal] =
        useState<boolean>(false);
    const [groupMembersData, setGroupMembersData] = useState<
        GroupMembersData[]
    >([]);
    const { mutate: updateUserGroup } = useUpdateUserGroup();
    const { mutate: deleteUserGroup } = useDeleteUserGroup();

    const {
        handleSubmit,
        control,
        setValue,
        watch,
        getValues,
        formState: { errors },
    } = useForm<CreateAndUpdateUserGroupFormData>({
        mode: 'all',
        resolver: yupResolver(CreateAndUpdateUserGroupSchema),
        defaultValues: createAndUpdateUserGroupDefaults,
    });

    useEffect(() => {
        if (hasUserCurrentRole('sdzGovernance')) {
            setReadOnly(false);
        } else {
            setReadOnly(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let combinedGroupMembers: GroupMembersData[] = [];
        if (!walletsLoading && walletsData) {
            combinedGroupMembers =
                combinedSystemAndUserWalletsData(walletsData);
            setGroupMembersData(combinedGroupMembers);
        }
        if (!userGroupDetailsLoading && !walletsLoading) {
            setValue('name', userGroupDetails?.data.name);
            setValue('description', userGroupDetails?.data.description);
            if (userGroupDetails?.data?.group?.length) {
                const groupsData = userGroupDetails.data.group
                    .map((groupId: string) => {
                        return combinedGroupMembers.find(
                            (groupMember) => groupId === groupMember.id
                        );
                    })
                    .filter(Boolean);
                setValue('group', groupsData);
            }
        }
        setIsDataLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userGroupDetailsLoading, walletsLoading]);

    const handleUserGroupNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=${USER_GROUPS}`,
            searchParams
        );
    };

    const onSubmit = (data: CreateAndUpdateUserGroupFormData): void => {
        setIsChangesUnsaved(false);
        setIsLoading(true);
        updateUserGroup(
            {
                token: getToken(),
                name: data.name.trim(),
                description: data.description ? data.description.trim() : '',
                group: data.group.map((item: any) => item.id),
                id: id as string,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    setTimeout(() => {
                        handleUserGroupNavigation();
                    }, 500);
                },
                onError: () => {
                    setIsLoading(false);
                    setIsChangesUnsaved(true);
                },
            }
        );
    };

    const handleDeleteButton = (): void => {
        setTimeout(() => {
            setIsChangesUnsaved(false);
            handleUserGroupNavigation();
        }, 500);
        deleteUserGroup({
            token: getToken(),
            id: id as string,
        });
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            handleUserGroupNavigation();
        }
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        handleUserGroupNavigation();
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleRemoveMembers = (groupId: string): void => {
        setIsChangesUnsaved(true);
        const existingGroupMembers = watch('group');
        let updatedGroupMembers = [];
        updatedGroupMembers = existingGroupMembers.filter(
            (member: GroupMembersData) => member.id !== groupId
        );
        setValue('group', updatedGroupMembers);
    };

    const handleAddGroupMembersModal = (): void => {
        setOpenAddGroupMemberModal(!openAddGroupMemberModal);
    };

    const onAddNewGroupMembers = (values: GroupMembersData[]): void => {
        setIsChangesUnsaved(true);
        let combinedGroupMembers = [...watch('group'), ...values];
        setValue('group', combinedGroupMembers);
    };

    const combinedLoading =
        userGroupDetailsLoading || walletsLoading || isDataLoading;

    return (
        <UpdateWalletsAndGroupsWrapper
            title={readOnly ? 'View User Group' : 'Edit User Group'}
            readOnly={readOnly}
            combinedLoading={combinedLoading}
            breadCrumData={'User Group'}
            handleCancelButton={handleCancelButton}
            handleCancelModal={handleCancelModal}
            onBackArrowClick={handleCancelButton}
            deleteMenuItemText={'Delete Group'}
            headingText={'General Info'}
            onSaveButtonClick={handleSubmit(onSubmit)}
            handleCancelModalConfirmation={handleCancelModalConfirmation}
            isLoading={isLoading}
            isChangesUnsaved={isChangesUnsaved}
            openCancelModal={openCancelModal}
            unsavedChangesConfirmationText={
                'This user group has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
            }
            confirmationKey={getValues('name')}
            confirmationButtonText={'Delete User Group'}
            confirmationHeadingText={'Confirm User Group Deletion'}
            confirmationInputLabelText={`Type ${getValues('name')} to confirm`}
            handleConfirmation={handleDeleteButton}
            confirmationSubHeadingText={
                <Typography>
                    By typing{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        ‘{getValues('name')}’
                    </Typography>{' '}
                    below, you understand that you are permanently deleting this
                    user group. This action{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        CANNOT
                    </Typography>{' '}
                    be undone.
                </Typography>
            }
        >
            <Grid
                sx={{
                    ...walletsAndGroupsCommonStyles.containerStyles,
                    p: readOnly ? '9px 31px 22px 31px' : '20px 35px 22px 32px',
                }}
            >
                <LabelAndValueComponent label={'ID'} value={id as string} />
                {readOnly ? (
                    <LabelAndValueComponent
                        label={'Group Name'}
                        value={getValues('name')}
                    />
                ) : (
                    <Grid sx={walletsAndGroupsCommonStyles.textFieldStyles}>
                        <Controller
                            name='name'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <TextField
                                    {...field}
                                    variant='outlined'
                                    label='Group Name'
                                    required
                                    onBlur={(): void => {
                                        setValue(
                                            'name',
                                            field.value.trimStart()
                                        );
                                    }}
                                    error={!!errors.name}
                                    errorText={
                                        errors.name ? errors.name.message : ''
                                    }
                                    onChange={(value): void => {
                                        field.onChange(value);
                                        setIsChangesUnsaved(true);
                                    }}
                                />
                            )}
                        />
                    </Grid>
                )}
                {readOnly ? (
                    <LabelAndValueComponent
                        label={'Description'}
                        value={
                            getValues('description')
                                ? getValues('description')
                                : '- -'
                        }
                    />
                ) : (
                    <DescriptionTextField
                        control={control}
                        rows={5}
                        error={errors.description}
                        required={false}
                        onChange={(): void => {
                            setIsChangesUnsaved(true);
                        }}
                    />
                )}
            </Grid>
            <Grid sx={updateUserGroupStyles.groupMembersTextContainerStyles}>
                <Typography sx={updateUserGroupStyles.groupMembersTextStyles}>
                    Group Members
                </Typography>
            </Grid>
            <Grid
                sx={{
                    ...updateUserGroupStyles.mainContainerStyles,
                    p: '26.16px 23px 24px 29px',
                }}
            >
                <SectionListWithSearchField
                    listData={watch('group')}
                    textFieldLabel='Search Members'
                    listDataError={errors.group}
                    sectionTitleKey='header'
                    searchKeys={['header', 'subtitle']}
                    emptyErrorHeadingText='Need at least one group member.'
                    emptyErrorSubHeadingText='Group members cannot be empty.'
                    searchEmptyHeadingText='No matching group member.'
                    searchEmptySubHeadingText='Try searching by name or email address.'
                    renderListItemComponent={(value: any): React.ReactNode => (
                        <ListComponent
                            value={value}
                            handleRemoveData={handleRemoveMembers}
                            readOnly={readOnly}
                        />
                    )}
                />
                {readOnly ? null : (
                    <Grid
                        sx={
                            updateUserGroupStyles.addMemberButtonContainerStyles
                        }
                    >
                        <Fab
                            type='button'
                            sx={updateUserGroupStyles.addMemberButtonStyles}
                            icon={<AddIcon />}
                            color='secondary'
                            label='Add Members'
                            onClick={handleAddGroupMembersModal}
                        />
                    </Grid>
                )}
            </Grid>
            {openAddGroupMemberModal && (
                <AddGroupMembers
                    openAddGroupMemberModal={openAddGroupMemberModal}
                    handleAddGroupMembersModal={handleAddGroupMembersModal}
                    existingGroupMembers={watch('group')}
                    groupMembersData={groupMembersData}
                    onAddNewGroupMembers={onAddNewGroupMembers}
                />
            )}
        </UpdateWalletsAndGroupsWrapper>
    );
};
